<template>
  <div style="margin: 0px 20px;">
    <!-- Toolbar -->
    <div
      v-if="commandColumns.length > 0"
      class="d-flex justify-content-end mb-2"
    >
      <button
        type="button"
        class="btn btn-sm mb-0 d-flex align-items-center"
        :class="showCommands ? 'bg-gradient-info text-white' : 'btn-outline-info'"
        @click="showCommands = !showCommands"
      >
        <i class="material-icons-round notranslate me-1" style="font-size: 18px;">
          {{ showCommands ? "visibility_off" : "visibility" }}
        </i>
        {{ showCommands ? "Ocultar colunas de conteúdo" : "Mostrar colunas de conteúdo" }}
        <span class="badge bg-white text-info ms-2">{{ commandColumns.length }}</span>
      </button>
    </div>

    <div class="table-responsive align-items-center mb-0">
      <table class="table table-hover align-items-center mb-0">
        <thead>
          <tr>
            <!-- Expand control -->
            <th class="text-center px-2 sticky-col sticky-col--expand"></th>

            <!-- Registration columns (dynamic) -->
            <th
              v-for="(col, index) in registrationColumns"
              :key="`reg-${col.key}`"
              class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3 col-header"
              :class="{ 'sticky-col sticky-col--first': index === 0 }"
            >
              <span class="col-header-label" :title="col.label">{{ col.label }}</span>
            </th>

            <!-- Fixed engagement column (sort key from backend) -->
            <th
              class="text-uppercase text-dark text-xxs font-weight-bolder opacity-9 text-center col-header"
            >
              <span class="col-header-label">Total de acessos</span>
            </th>

            <!-- Command columns (dynamic) -->
            <template v-if="showCommands">
              <th
                v-for="col in commandColumns"
                :key="`cmd-${col.key}`"
                class="text-uppercase text-info text-xxs font-weight-bolder opacity-9 text-center col-header"
              >
                <span class="col-header-label" :title="col.label">{{ col.label }}</span>
              </th>
            </template>
          </tr>
        </thead>

      <tbody>
        <template v-for="row in rows" :key="row.student_id">
          <tr>
            <!-- Expand control -->
            <td class="text-center align-middle px-2 sticky-col sticky-col--expand">
              <button
                v-if="hasChildrenDetail(row)"
                class="btn btn-link p-0 m-0 text-info"
                type="button"
                @click="toggle(row.student_id)"
                :aria-expanded="isExpanded(row.student_id)"
                :title="isExpanded(row.student_id) ? 'Recolher' : 'Ver detalhes dos comandos'"
              >
                <i class="material-icons-round notranslate" style="font-size: 22px; line-height: 1;">
                  {{ isExpanded(row.student_id) ? 'expand_less' : 'expand_more' }}
                </i>
              </button>
            </td>

            <!-- Registration cells (dynamic) -->
            <td
              v-for="(col, index) in registrationColumns"
              :key="`reg-${row.student_id}-${col.key}`"
              class="align-middle text-sm text-dark ps-3"
              :class="{ 'sticky-col sticky-col--first': index === 0 }"
            >
              <span v-if="hasValue(row.registration, col.key)">
                {{ row.registration[String(col.key)] }}
              </span>
              <span v-else class="text-secondary opacity-6">&mdash;</span>

              <!-- Enrollment date: fixed row field, shown under the first column -->
              <div
                v-if="index === 0 && row.enrollment_created"
                class="text-xs text-secondary opacity-8 mt-1"
              >
                Matrícula: {{ formatDateShort(row.enrollment_created) }}
              </div>
            </td>

            <!-- Fixed engagement cell -->
            <td class="align-middle text-center">
              <span class="text-lg font-weight-bold text-dark">{{ row.total_accesses || 0 }}</span>
              <div v-if="maxLastAccess(row)" class="text-xs text-secondary mt-1">
                {{ formatDate(maxLastAccess(row)) }}
              </div>
            </td>

            <!-- Command cells (dynamic) -->
            <template v-if="showCommands">
              <td
                v-for="col in commandColumns"
                :key="`cmd-${row.student_id}-${col.key}`"
                class="align-middle text-center"
              >
                <template v-if="getCommand(row, col.key)">
                  <span
                    class="badge badge-sm bg-gradient-info"
                    :title="`Inclui acessos no menu e em todos os comandos filhos`"
                  >
                    {{ getCommand(row, col.key).accesses }}
                    {{ getCommand(row, col.key).accesses === 1 ? 'acesso' : 'acessos' }}
                  </span>
                  <div class="text-xs text-secondary mt-1">
                    {{ formatDate(getCommand(row, col.key).last_access) }}
                  </div>
                </template>
                <span v-else class="text-secondary opacity-6">0</span>
              </td>
            </template>
          </tr>

          <!-- Drill-down: expanded detail row -->
          <tr v-if="isExpanded(row.student_id)" :key="`detail-${row.student_id}`">
            <td :colspan="totalColumns" class="bg-light-info p-0">
              <div class="p-3">
                <h6 class="text-sm text-dark mb-3">
                  Detalhamento de acessos &middot; {{ row.name }}
                </h6>

                <div class="row">
                  <div
                    v-for="col in commandColumns"
                    :key="`detail-${row.student_id}-${col.key}`"
                    class="col-12 col-md-6 col-xl-4 mb-3"
                  >
                    <div class="card shadow-none border h-100">
                      <div class="card-body p-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-sm font-weight-bold text-dark">{{ col.label }}</span>
                          <span class="badge badge-sm bg-gradient-info">
                            {{ getCommand(row, col.key) ? getCommand(row, col.key).accesses : 0 }}
                          </span>
                        </div>

                        <ul
                          v-if="getChildren(row, col.key).length"
                          class="list-unstyled mb-0"
                        >
                          <li
                            v-for="child in getChildren(row, col.key)"
                            :key="child.key"
                            class="d-flex justify-content-between align-items-center text-xs py-1 border-bottom"
                          >
                            <span class="text-dark">{{ child.label }}</span>
                            <span class="text-secondary ms-2 text-end">
                              {{ child.accesses }}
                              {{ child.accesses === 1 ? 'acesso' : 'acessos' }}
                              <br />
                              {{ formatDate(child.last_access) }}
                            </span>
                          </li>
                        </ul>

                        <span v-else class="text-xs text-secondary opacity-7">
                          Sem acesso em comandos específicos.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "ParticipantsContentTreeTable",

  props: {
    columns: {
      type: Object,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      expanded: {},
      showCommands: false, // content (blue) columns start hidden
    };
  },

  computed: {
    registrationColumns() {
      return (this.columns && this.columns.registration) || [];
    },

    commandColumns() {
      return (this.columns && this.columns.commands) || [];
    },

    totalColumns() {
      // expand control + registration + total_accesses + (commands if visible)
      const commands = this.showCommands ? this.commandColumns.length : 0;
      return 1 + this.registrationColumns.length + 1 + commands;
    },
  },

  methods: {
    isExpanded(studentId) {
      return this.expanded[studentId] === true;
    },

    toggle(studentId) {
      this.expanded[studentId] = !this.expanded[studentId];
    },

    // Registration value present? Treat null / "" as empty.
    hasValue(registration, key) {
      if (!registration) return false;
      const value = registration[String(key)];
      return value !== null && value !== undefined && value !== "";
    },

    // Sparse object: command for this parent key, or null if no activity.
    getCommand(row, key) {
      if (!row.commands) return null;
      return row.commands[String(key)] || null;
    },

    getChildren(row, key) {
      const command = this.getCommand(row, key);
      return (command && command.children) || [];
    },

    // Any parent command with children to show in the drill-down?
    hasChildrenDetail(row) {
      if (!row.commands) return false;
      return Object.keys(row.commands).length > 0;
    },

    // No per-participant "last access": derive the most recent among parents.
    maxLastAccess(row) {
      if (!row.commands) return null;
      let latest = null;
      Object.values(row.commands).forEach((command) => {
        if (!command || !command.last_access) return;
        const current = new Date(command.last_access);
        if (isNaN(current.getTime())) return;
        if (latest === null || current > latest) latest = current;
      });
      return latest;
    },

    formatDate(isoDate) {
      if (!isoDate) return "—";
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDateShort(isoDate) {
      if (!isoDate) return "—";
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.bg-light-info {
  background-color: #f0f8ff;
}

table thead th {
  border-bottom: 1px solid #e9ecef;
  vertical-align: bottom;
}

/* Long headers: give columns room and wrap to at most 2 lines.
   The min-width also sets the column floor: a roomier floor makes the
   horizontal scroll appear before columns get squeezed. */
.col-header {
  min-width: 170px;
  max-width: 240px;
}

.col-header-label {
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.25;
}

table td {
  white-space: normal;
  overflow-wrap: break-word;
  word-wrap: break-word;
}

/* Sticky columns: keep participant identity in view while scrolling sideways. */
.sticky-col {
  position: sticky;
  background-color: #fff;
  z-index: 2;
}

thead .sticky-col {
  z-index: 3;
}

.sticky-col--expand {
  left: 0;
  width: 44px;
  min-width: 44px;
}

.sticky-col--first {
  left: 44px;
  /* box-shadow instead of border-right: reliable with border-collapse and
     renders consistently on every row (including the last one).
     The second shadow re-applies Bootstrap's row hover/stripe overlay
     (--bs-table-accent-bg), which our own box-shadow would otherwise replace. */
  box-shadow: inset -1px 0 0 0 #e9ecef,
    inset 0 0 0 9999px var(--bs-table-accent-bg);
}
</style>
